import React from 'react';
import { motion } from 'framer-motion';

interface ProgressLoaderProps {
  percentage: number;
  message: string;
  title?: string;
  onCancel?: () => void;
  success?: boolean;
}

const ProgressLoader: React.FC<ProgressLoaderProps> = ({
  percentage,
  message,
  title = 'Just a minute...',
  onCancel,
  success = false
}) => {
  // Ensure minimum 25% progress for better UX
  const displayPercentage = Math.max(percentage, 10);
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-surface-dark border border-gray-700 rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl"
      >
        {/* Icon */}
        <div className="flex items-center gap-4 mb-6">
          <div className={`w-12 h-12 rounded-full ${success ? 'bg-green-500' : 'bg-brand-primary'} flex items-center justify-center`}>
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white">{title}</h3>
            <p className={`text-sm mt-1 ${success ? 'text-green-400 font-medium' : 'text-gray-400'}`}>{message}</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="w-full h-2 bg-surface-medium rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${displayPercentage}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="h-full bg-brand-primary rounded-full relative overflow-hidden"
            >
              {/* Shimmer Animation */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                animate={{
                  x: ['-100%', '200%']
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'linear'
                }}
              />
            </motion.div>
          </div>
          <div className="mt-2 text-right">
            <span className="text-sm font-medium text-white">{displayPercentage}%</span>
          </div>
        </div>

        {/* Cancel Button */}
        {onCancel && (
          <button
            onClick={onCancel}
            className="w-full mt-4 px-4 py-2 bg-surface-medium text-gray-300 cursor-pointer rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
          >
            Cancel
          </button>
        )}
      </motion.div>
    </div>
  );
};

export default ProgressLoader;
